import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Input,
  message,
  Space,
  Statistic,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { ShopRequest } from "../../../interfaces/shop-request.interface";
import shopService from "../../../services/shop.service";
import ShopRequestTable from "../../tables/shop-request-table";

interface ShopRequestsProps {}

const ShopRequests: React.FC<ShopRequestsProps> = () => {
  const [shopRequests, setShopRequests] = useState<ShopRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<ShopRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const fetchShopRequests = useCallback(async () => {
    try {
      setLoading(true);
      const requests = await shopService.getShopRequests();
      setShopRequests(requests || []);
    } catch (error) {
      message.error("Shop requests could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShopRequests();
  }, [fetchShopRequests]);

  useEffect(() => {
    const keyword = searchText.trim().toLowerCase();

    if (!keyword) {
      setFilteredRequests(shopRequests);
      return;
    }

    const filtered = shopRequests.filter((item) =>
      [
        item.ownerName,
        item.name,
        item.address,
        item.email,
        item.phone,
        item.description,
        item.userId,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword)),
    );

    setFilteredRequests(filtered);
  }, [shopRequests, searchText]);

  const websiteRequestCount = useMemo(() => {
    return shopRequests.filter((item) => item.userId === "whappo-website")
      .length;
  }, [shopRequests]);

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 24,
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
        background:
          "linear-gradient(180deg, #ffffff 0%, #fcfcfd 60%, #f8fafc 100%)",
      }}
      bodyStyle={{ padding: 24 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <Space direction="vertical" size={4}>
          <Space size={10} align="center">
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(24, 144, 255, 0.08)",
              }}
            >
              <ShopOutlined style={{ fontSize: 20 }} />
            </div>

            <div>
              <Typography.Title
                level={3}
                style={{ margin: 0, fontSize: 24, lineHeight: 1.2 }}
              >
                Shop Requests
              </Typography.Title>
              <Typography.Text type="secondary">
                Review incoming store requests and turn them into active shops.
              </Typography.Text>
            </div>
          </Space>
        </Space>

        <Space size={12} wrap>
          <Card
            bordered={false}
            style={{
              minWidth: 140,
              borderRadius: 18,
              background: "#f8fafc",
              boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.04)",
            }}
            bodyStyle={{ padding: "14px 18px" }}
          >
            <Statistic title="Total Requests" value={shopRequests.length} />
          </Card>

          <Card
            bordered={false}
            style={{
              minWidth: 140,
              borderRadius: 18,
              background: "#f8fafc",
              boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.04)",
            }}
            bodyStyle={{ padding: "14px 18px" }}
          >
            <Statistic title="From Website" value={websiteRequestCount} />
          </Card>
        </Space>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 18,
          flexWrap: "wrap",
        }}
      >
        <Input
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search owner, shop, email, phone, address..."
          prefix={<SearchOutlined />}
          style={{
            maxWidth: 420,
            height: 42,
            borderRadius: 14,
          }}
        />

        <Button
          icon={<ReloadOutlined />}
          onClick={fetchShopRequests}
          loading={loading}
          style={{
            height: 42,
            borderRadius: 14,
            fontWeight: 600,
          }}
        >
          Refresh
        </Button>
      </div>

      <ShopRequestTable shopRequests={filteredRequests} loading={loading} />
    </Card>
  );
};

export default ShopRequests;
